import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Everyone should learn [about] programming",
  "description": "",
  "published": "2012-02-01T00:00:00.000Z",
  "redirect_from": ["/blog/everyone-should-learn-about-programming/swizec/3608"],
  "categories": "Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`What use have an artist, a baker, a winemaker, a firefighter and a brewer with `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Computer_programming",
        "title": "Computer programming"
      }}>{`programming`}</a>{`? None.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/sissons_brewer_199710.jpg",
        "alt": "A brewer",
        "title": "A brewer"
      }}></img></p>
    <p>{`Yet.`}</p>
    <p>{`An article surfaced on `}<a parentName="p" {...{
        "href": "http://news.ycombinator.com/",
        "title": "Hacker News"
      }}>{`HackerNews`}</a>{` yesterday arguing that `}<a parentName="p" {...{
        "href": "http://news.ycombinator.com/item?id=3528940"
      }}>{`Programming is the new High School Diploma`}</a>{`, the main argument being two facts:`}</p>
    <ol>
      <li parentName="ol">{`Computers are everywhere`}</li>
      <li parentName="ol">{`Automation is pushing out middle-tier jobs`}</li>
    </ol>
    <p>{`The basic idea is really simple - in the not very distant future programming will and should be as important as basic literacy is today. Not just basic computer literacy, but programming in the sense of combining and extending apps in novel ways to fit your problem area.`}</p>
    <p>{`There are a lot of professions for which automation is almost impossible to imagine - an artist, a firefighter, a winemaker, brewer, baker; the list goes on and on. In fact this was the top voted rebuttal of the whole story. A lot of professions need so much human touch it is inconceivable they would ever need programming.`}</p>
    <h2 {...{
      "id": "someone-else-can-do-the-coding"
    }}>{`Someone else can do the coding`}</h2>
    <p>{`And if parts of their jobs do become automated, they still won't need programming because there will be special people to program special tools for these professions and just show them how they're used.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/300px-DC_firefighter2.jpg",
        "alt": "Washington, D.C. firefighter",
        "title": "Washington, D.C. firefighter"
      }}></img></p>
    <p>{`All well and good, but that's missing the point entirely.`}</p>
    <p>{`There is no need for an artist, or a baker, or anyone working in meatspace to be a good programmer. They have no need for `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Pointer_%28computing%29",
        "title": "Pointer (computing)"
      }}>{`pointer arithmetic`}</a>{`, `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Functional_programming",
        "title": "Functional programming"
      }}>{`functional programming`}</a>{`, `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/P_versus_NP_problem",
        "title": "P versus NP problem"
      }}>{`P=NP`}</a>{` or knowing javascript and python's list comprehensions.`}</p>
    <p>{`They just don't.`}</p>
    <p>{`Somebody else can do all that stuff - an actual expert. But how much does a programmer sitting at home, watching code and cat pictures all day, know about the problems of a baker or an artist? Or about fighting fires?`}</p>
    <h2 {...{
      "id": "problem-areas"
    }}>{`Problem areas`}</h2>
    <p>{`You can't solve problems you don't know exist!`}</p>
    <p>{`Someone with problems who doesn't know of a whole field of solutions can't ask for help either.`}</p>
    <p>{`And so we reach an impasse. On the one hand you have an industry full of people so hungry for problems they create better and better ways to share ever more boring stuff, and on the other hand you have ... the other ~70% of the population drowning in real, hard, solvable problems.`}</p>
    <p>{`While you can check foursquare to see where your friends are, there are firefighters who would (probably?) love nothing more than a real-time map of where all their buddies are in a building. And what are the best and brightest of the world doing? Nothing much, I heard there's a meme making rounds about something or another.`}</p>
    <p>{`High school is about broadness and giving pupils a little bit of everything. Some history to understand the problems with large-scale ethnicism, a little bit of biology to help you talk to the doctor, a dash of this and that. All in all, none of us become experts in every field we study in high school, but studying them gives us a better understanding of the world.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2012/02/chef1.jpg",
        "alt": "A chef",
        "title": "A chef"
      }}></img></p>
    <p>{`And yet high school deprives us of even the simplest knowledge of a field touching `}<em parentName="p">{`everything`}</em>{` in our lives.`}</p>
    <p>{`To innovate, to even begin to really use this amazing tool, we need to give the general populace the ability to begin the process of automation - that spark of `}<em parentName="p">{`Hey, this is kind of repetitive, I wonder if a computer could do it.`}</em></p>
    <p>{`Chances are it could.`}</p>
    <p>{`In fact, we know a computer can solve any problem we consider solvable ... even subsets of those that aren't solvable. But you first need someone who knows how to actually solve a problem.`}</p>
    <p>{`That's why most problems `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Programmer",
        "title": "Programmer"
      }}>{`programmers`}</a>{` solve are those other programmers have. It's like an echo chamber out there, nothing but better and quicker ways to make development easier. These are then used to make development even easier and suddenly it's become `}<em parentName="p">{`impossible`}</em>{` to keep up with the world of computing.`}</p>
    <p>{`Most other industries innovate much more slowly. Most of them can't solve their own problems and they don't even realize a solution exists because they don't realize they have a problem. The greatest handbrake on innovation is a worker with too narrow a worldview to understand their job could be made better.`}</p>
    <h2 {...{
      "id": "what-you-can-do"
    }}>{`What you can do?`}</h2>
    <p>{`Because changing how high school works is hard, I have a suggestion for something you can do `}<strong parentName="p">{`right now`}</strong>{`! Only needs three steps too`}</p>
    <ol>
      <li parentName="ol">{`Find a nonprogrammer friend`}</li>
      <li parentName="ol">{`Take them out for a beer`}</li>
      <li parentName="ol">{`Teach them about programming`}</li>
    </ol>
    <p>{`Give them the bug of automation. Do it with passion. Do it well. When they start looking at the world in terms of `}<em parentName="p">{`Hmm ... I wonder how this could be made better, easier, more interesting. `}{`_`}{`They will start coming back with real problems. Problems out of their solution scope, but something `}{`_`}{`you`}</em>{` can work on.`}</p>
    <p>{`And hey, startup opportunity! You now have somebody with a real problem looking for a real solution! Hooray.`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.nj.com/news/index.ssf/2012/01/firefighters_investigate_secon.html"
        }}>{`Firefighters investigate second suspicious fire at vacant New Brunswick high school`}</a>{` (nj.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://zeroturnaround.com/blog/developer-productivity-report-part-1-developer-timesheet/"
        }}>{`imabonehead: Developer Productivity Report - Part 1: Developer Timesheet | zeroturnaround.com`}</a>{` (zeroturnaround.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://www.answers.com/topic/corpus-answers-guides-how-to-become-a-firefighter"
        }}>{`How to Become a Firefighter`}</a>{` (answers.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/programmers-are-fucking-lazy/swizec/2648"
        }}>{`Programmers are fucking lazy`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://swizec.com/blog/programmers-are-born-not-made/swizec/3369"
        }}>{`Programmers are born not made`}</a>{` (swizec.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://cdixon.org/2012/01/31/who-should-learn-to-program/"
        }}>{`Who should learn to program?`}</a>{` (cdixon.org)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=663f89e2-33c8-49cd-9b31-27270739f9ab",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      